import { GMI, STATE } from "../../../constants";
import replacement from "../../../services/gma/replacement";
import { types } from "../../../utils/js/types";
import { batchActions } from "../../../utils/redux";
import request from "../../request";

const { REPLACEMENT } = STATE.UI;

/**
 * @typedef { "country_code" | "country_subdivision_code" | "city" | "postal" | "street_address" | "street_address_two" } FieldNames
 */

/** @type {Record<FieldNames, string[]>} */
const FIELD_PATHS = {
  country_code:
    REPLACEMENT.LOCATION.SUBMIT.PICKUP_DELIVERY_ADDRESS_COUNTRY_CODE,
  country_subdivision_code:
    REPLACEMENT.LOCATION.SUBMIT
      .PICKUP_DELIVERY_ADDRESS_COUNTRY_SUBDIVISION_CODE,
  city: REPLACEMENT.LOCATION.SUBMIT.PICKUP_DELIVERY_ADDRESS_CITY,
  postal: REPLACEMENT.LOCATION.SUBMIT.PICKUP_DELIVERY_ADDRESS_POSTAL,
  street_address:
    REPLACEMENT.LOCATION.SUBMIT.PICKUP_DELIVERY_ADDRESS_STREET_ADDRESS,
  street_address_two:
    REPLACEMENT.LOCATION.SUBMIT.PICKUP_DELIVERY_ADDRESS_STREET_ADDRESS_TWO
};

/**
 * Action creator for setting field
 * @private
 * @param {FieldNames} field - Dot notation path of the field
 * @param {unknown} value - Value to set
 * @returns {Object} Action object
 */
const setField = (field, value) => ({
    type: GMI.SET_GMI_STATE,
    path: FIELD_PATHS[field],
    value
  });
/**
 * Action creator for clearing field
 * @private
 * @param {FieldNames} field - Dot notation path of the field
 * @returns {Object} Action object
 */
const clearField = (field) => ({
  type: GMI.SET_GMI_STATE,
  path: FIELD_PATHS[field],
  value: null
});

/**
 * Batch actions for setting state
 * @private
 * @param {Record<FieldNames, unknown>} fields - Key-value pairs of fields and values to set
 * @returns {Object} Batched action that sets each passed field
 */
const setState = (fields = {}) => {
  const batchedActions = [];

  Object.entries(fields).forEach(([key, value]) => {
    if (FIELD_PATHS[key] && value) {
      batchedActions.push(setField(key, value));
    }
  });

  return batchActions(...batchedActions);
};

/**
 * Clears the top-level state
 * @private
 * @returns {Object} Action object
 */
function clearState() {
  const batchedActions = [];

  Object.keys(FIELD_PATHS).forEach((field) => {
    batchedActions.push(clearField(field));
  });

  return batchActions(...batchedActions);
}

export const locationSubmitPickupDeliveryAddressState = {
  /**
   * @name set
   * @desc A global setter for ui.replacement.location.submit.pickup_delivery_address data
   * @param {String} [type] - Type of the field to set
   * @param {Object} [args] - Value to set
   * @returns {Function} Dispatch function
   * @memberof actions.replacement
   * @example
   *
   * // Set country_code data for location submit state
   * store.dispatch(actions.replacement.locationSubmitPickupDeliveryAddressState.set('country_code', 'US'));
   *
   * // Set location submit state
   * store.dispatch(actions.replacement.locationSubmitPickupDeliveryAddressState.set(null, {
   *   country_code: 'US',
   *   city: 'New York',
   * }));
   */
  set(field = null, value = {}) {
    return (dispatch) => {
      if (!types(field).isString) return dispatch(setState(value));
      if (FIELD_PATHS[field]) return dispatch(setField(field, value));
    };
  },

  /**
   * @name unset
   * @desc A global unsetter for ui.replacement.location.submit.pickup_delivery_address data
   * @param {String} [type] - Type of the field to unset
   * @returns {Function} Dispatch function
   * @memberof actions.replacement
   * @example
   *
   * // Unset country_code data for location submit state
   * store.dispatch(actions.replacement.locationSubmitPickupDeliveryAddressState.unset('country+code'));
   *
   * // Unset location submit state
   * store.dispatch(actions.replacement.locationSubmitPickupDeliveryAddressState.unset());
   *
   */
  unset(field = null) {
    return (dispatch) => {
      if (!types(field).isString) return dispatch(clearState());
      if (FIELD_PATHS[field]) return dispatch(clearField(field));
    };
  }
};

/**
 * Gets data from session to be saved to gmi.ui.reservations.location.submit.pickup_delivery_address state
 * @param {Immutable} session - GMA session
 * @returns {Record<FieldNames, unknown>}
 */
export function getLocationSubmitPickupDeliveryAddressState(session) {
  const pickupDeliveryAddress = session
    .getIn(["replacement", "ticket", "pickup_delivery_address"])
    ?.toJS();

  if (!pickupDeliveryAddress) return;

  return {
    country_code: pickupDeliveryAddress?.street_address_two,
    country_subdivision_code: pickupDeliveryAddress?.country_subdivision_code,
    city: pickupDeliveryAddress.city,
    postal: pickupDeliveryAddress.postal,
    street_address: pickupDeliveryAddress.street_addresses?.[0],
    street_address_two: pickupDeliveryAddress.street_addresses?.[1],
  };
}

/**
 * Calls Pickup Location service with Custom Location payload
 * @example store.dispatch(actions.replacement.location.pickupLocationPickupDeliveryAddress());
 * @returns {Function} Dispatched service action
 */
export function pickupLocationPickupDeliveryAddress() {
  return (dispatch, getState) => {
    const data = getState().getIn(REPLACEMENT.LOCATION.SUBMIT.PATH).toJS();

    return dispatch(
      request({
        service: replacement.pickupLocation,
        data
      })
    );
  };
}
